/**
 * Turning the allergy and safety-flag forms' codes into words (DECISIONS.md #46).
 *
 * Nothing here holds state and nothing renders, matching `registrationDisplay.ts`: the forms read
 * these, so the mapping from a refusal code to a message is testable without a DOM and the wording
 * is reviewable in one place.
 *
 * **Every code gets a message.** {@link allergyRefusalMessage} and {@link flagRefusalMessage} are
 * exhaustive over their unions by construction, so a code added on the server and typed into the api
 * module fails the typecheck here until somebody has written the wording. A default branch would be
 * a blank message on the panel that is supposed to say *penicillinallergi*.
 *
 * **One module for two api modules**, which is a deliberate break from the file-per-feature pattern
 * the rest of `patient/` follows. Allergies and safety flags are one panel on one screen and one
 * `safety` catalogue, and nine of their refusals are the same sentence — *the patient is not in the
 * record*, *your role may not document*, *nothing was saved, leave the form open*. Two copies of
 * those with nothing keeping them equal is wording that drifts the first time a reviewer rewords
 * one, and this is clinician-facing text about a write that cannot be undone.
 */

import type { TFunction } from 'i18next'

import type { AllergyRefusalCode, AllergyRefusalParams } from './allergyApi'
import type { FlagRefusalCode, FlagRefusalParams } from './safetyFlagApi'

/**
 * The lookup the two forms need.
 *
 * **`patient` is in the list on purpose**, for the reason `registrationDisplay.ts` gives: the words
 * for the flag types, the allergy severities and the criticalities already exist there because the
 * overview renders them, and the option a clinician picks must be the same word the value is then
 * read under. Two catalogue entries holding `svår` with nothing keeping them equal is a label that
 * drifts.
 */
export type SafetyTranslate = TFunction<['common', 'patient', 'safety']>

/** The allergy fields, by the name they travel under in a `FIELD_TOO_LONG` param. */
const ALLERGY_FIELDS = ['substance', 'reaction'] as const

/** The safety-flag fields, likewise. */
const FLAG_FIELDS = ['value', 'note'] as const

/** An allergy field's label, reused as the name in a "too long" message. */
export function allergyFieldLabel(field: string, t: SafetyTranslate): string {
  switch (field) {
    case 'substance':
      return t('safety:allergy.fieldSubstance')
    case 'reaction':
      return t('safety:allergy.fieldReaction')
    case 'severity':
      return t('safety:allergy.fieldSeverity')
    case 'criticality':
      return t('safety:allergy.fieldCriticality')
    case 'verification':
      return t('safety:allergy.fieldVerification')
    case 'status':
      return t('safety:allergy.fieldStatus')
    default:
      // A server field this interface has not met. Named raw rather than dropped, matching
      // `registrationDisplay.ts`: the message's job is to say which box to shorten, and a field
      // that is named badly still beats a sentence with a gap in it.
      return field
  }
}

/** A safety-flag field's label, likewise. */
export function flagFieldLabel(field: string, t: SafetyTranslate): string {
  switch (field) {
    case 'value':
      return t('safety:flag.fieldValue')
    case 'note':
      return t('safety:flag.fieldNote')
    default:
      return field
  }
}

/**
 * An allergy status in words, for the form's select.
 *
 * `no-known-allergies` is offered as a status rather than as a separate button, because that is what
 * it is in the record: a row asserting the question was asked and the answer was no. The overview
 * reads it through the same vocabulary, which is why it is worded as an answer and not as an
 * absence.
 */
export function allergyStatusLabel(status: string, t: SafetyTranslate): string {
  switch (status) {
    case 'active':
      return t('safety:allergy.statusActive')
    case 'resolved':
      return t('safety:allergy.statusResolved')
    case 'no-known-allergies':
      return t('safety:allergy.statusNoKnownAllergies')
    case 'entered-in-error':
      return t('safety:allergy.statusEnteredInError')
    default:
      // The vocabulary is closed by migration 0005's CHECK, so this is unreachable from the form's
      // own lists. Showing the code beats hiding the option.
      return status
  }
}

/**
 * A severity in words, on the overview's keys.
 *
 * Those keys read as lower-case fragments — `svår` — because the panel renders them inside a
 * parenthesis. That is the right word in a select too, and picking a second capitalised copy would
 * be the drift this module exists to avoid.
 */
export function allergySeverityLabel(severity: string, t: SafetyTranslate): string {
  switch (severity) {
    case 'mild':
      return t('patient:allergy.severityMild')
    case 'moderate':
      return t('patient:allergy.severityModerate')
    case 'severe':
      return t('patient:allergy.severitySevere')
    default:
      return severity
  }
}

/** A criticality in words, on the overview's keys. */
export function allergyCriticalityLabel(criticality: string, t: SafetyTranslate): string {
  switch (criticality) {
    case 'low':
      return t('patient:allergy.criticalityLow')
    case 'high':
      return t('patient:allergy.criticalityHigh')
    case 'unable-to-assess':
      return t('patient:allergy.criticalityUnableToAssess')
    default:
      return criticality
  }
}

/** A verification state in words. */
export function allergyVerificationLabel(verification: string, t: SafetyTranslate): string {
  switch (verification) {
    case 'confirmed':
      return t('safety:allergy.verificationConfirmed')
    case 'unconfirmed':
      // The overview's word, for the same reason as the severities.
      return t('patient:allergy.unconfirmed')
    default:
      return verification
  }
}

/**
 * The empty choice in a vocabulary select: nobody has answered.
 *
 * **Not the same as any of the values**, and the hint under the field says so. A severity left empty
 * is a severity nobody established, and reading it as `mild` is exactly the wrong way to be wrong
 * about an allergy.
 */
export function notAnsweredLabel(t: SafetyTranslate): string {
  return t('safety:notAnswered')
}

/**
 * A flag type in words, on the overview's keys.
 *
 * The two types this form cannot create are here anyway: a correction form opens on a flag that is
 * already in the record, including an imported DNR, and it has to be able to name what it is showing.
 */
export function flagTypeLabel(flagType: string, t: SafetyTranslate): string {
  switch (flagType) {
    case 'dnr':
      return t('patient:safety.dnr')
    case 'treatment-limitation':
      return t('patient:safety.treatmentLimitation')
    case 'isolation':
      return t('patient:safety.isolation')
    case 'infection-risk':
      return t('patient:safety.infectionRisk')
    case 'fall-risk':
      return t('patient:safety.fallRisk')
    case 'allergy-status-unknown':
      return t('patient:safety.allergyStatusUnknown')
    case 'weight':
      return t('patient:safety.weight')
    default:
      // The list is closed in the database and open on screen — `patientOverviewApi.ts` says why. A
      // type a later migration added reaches the form as the code it is rather than as a blank.
      return flagType
  }
}

/** A flag status in words. */
export function flagStatusLabel(status: string, t: SafetyTranslate): string {
  switch (status) {
    case 'active':
      return t('safety:flag.statusActive')
    case 'resolved':
      return t('safety:flag.statusResolved')
    case 'entered-in-error':
      return t('safety:flag.statusEnteredInError')
    default:
      return status
  }
}

/**
 * The label on the field a flag's brief value goes in, which depends on the type.
 *
 * *Vikt (kg)* and *Typ av isolering* are different questions, and a form asking for `Värde` would be
 * asking the clinician to guess which. The three types that name nothing get no field at all, so
 * this is only reached for the two that do — and for an unknown type, where a neutral label is the
 * honest one.
 */
export function flagValueLabel(flagType: string, t: SafetyTranslate): string {
  switch (flagType) {
    case 'weight':
      return t('safety:flag.fieldWeight')
    case 'isolation':
      return t('safety:flag.fieldIsolationType')
    case 'treatment-limitation':
      return t('safety:flag.fieldLimitation')
    default:
      return t('safety:flag.fieldValue')
  }
}

/**
 * Whether a type carries a brief value at all — migration 0005's rule, mirrored.
 *
 * `treatment-limitation` is here because #46 point 4 lets its wording be corrected even though it
 * cannot be created: `safety_flag.value` carries the limitation in brief and `note` the reasoning,
 * which is the split the patient header renders.
 */
export function flagTakesValue(flagType: string): boolean {
  return flagType === 'weight' || flagType === 'isolation' || flagType === 'treatment-limitation'
}

/**
 * What to tell the clinician about a refused allergy write.
 *
 * The `UNKNOWN` case is not a fallback for laziness — it is what a proxy error page or a future code
 * reaches, and its message says the allergy was **not** saved, which is the one fact that matters and
 * is true of every refusal.
 */
export function allergyRefusalMessage(
  code: AllergyRefusalCode | 'UNKNOWN',
  params: AllergyRefusalParams,
  t: SafetyTranslate,
): string {
  switch (code) {
    case 'ALLERGY_STATUS_INVALID':
      return t('safety:allergy.refusal.statusInvalid')

    case 'ALLERGY_STATUS_NOT_ALLOWED':
      // `entered-in-error` as a first version: a row written in order to say it is wrong documents
      // nothing. The message names the way out, which is to document nothing at all.
      return t('safety:allergy.refusal.statusNotAllowed')

    case 'ALLERGY_SUBSTANCE_MISSING':
      return t('safety:allergy.refusal.substanceMissing')

    case 'ALLERGY_SUBSTANCE_NOT_ALLOWED':
      // A substance alongside `no-known-allergies`. Two mistakes with one shape, kept apart on the
      // server so this sentence can say which was made rather than *check the form*.
      return t('safety:allergy.refusal.substanceNotAllowed')

    case 'ALLERGY_SEVERITY_INVALID':
      return t('safety:allergy.refusal.severityInvalid')

    case 'ALLERGY_CRITICALITY_INVALID':
      return t('safety:allergy.refusal.criticalityInvalid')

    case 'ALLERGY_VERIFICATION_INVALID':
      return t('safety:allergy.refusal.verificationInvalid')

    case 'ALLERGY_FIELD_TOO_LONG':
      return t('safety:refusal.fieldTooLong', {
        // The server's limit, not a client constant, so a drift between the two reads as a
        // disagreement somebody can see rather than as a wrong number in a message.
        field: allergyFieldLabel(params.field ?? '', t),
        maximum: params.max ?? 0,
      })

    case 'ALLERGY_NOT_FOUND':
      return t('safety:allergy.refusal.notFound')

    case 'ALLERGY_RETRACTED':
      return t('safety:allergy.refusal.retracted')

    case 'ALLERGY_VERSION_STALE':
      return t('safety:refusal.versionStale')

    case 'ALLERGY_NO_CHANGE':
      return t('safety:refusal.noChange')

    case 'ALLERGY_SUPERSEDES_MISSING':
    case 'ALLERGY_SUPERSEDES_NOT_ALLOWED':
      // Both mean the form and the route disagree about whether this is a first version or a
      // correction, which is not something the clinician typed. One sentence for the pair, and it
      // says to reopen the patient.
      return t('safety:refusal.supersedesWrong')

    case 'PATIENT_NOT_FOUND':
      return t('safety:refusal.patientNotFound')

    case 'PATIENT_RETRACTED':
      return t('safety:refusal.patientRetracted')

    case 'ROLE_MAY_NOT_DOCUMENT':
      return t('safety:refusal.roleMayNotDocument')

    case 'ALLERGY_BODY_MISSING':
    case 'UNKNOWN':
      return t('safety:allergy.refusal.unknown')
  }
}

/** What to tell the clinician about a refused safety-flag write. */
export function flagRefusalMessage(
  code: FlagRefusalCode | 'UNKNOWN',
  params: FlagRefusalParams,
  t: SafetyTranslate,
): string {
  switch (code) {
    case 'FLAG_TYPE_INVALID':
      return t('safety:flag.refusal.typeInvalid')

    case 'FLAG_TYPE_NOT_DOCUMENTABLE':
      // #46 point 4: a DNR or a treatment limitation names who decided and when, and phase 0 has no
      // way to name a decider that is not the person typing. The form never offers these, so this is
      // reached only by a request that went round it — but the sentence still says why rather than
      // *not allowed*.
      //
      // Two wordings rather than an interpolated blank. The type is the **first word** of this
      // sentence, so a refusal that arrived without one would open with a space — and both branches
      // of that are reachable from a proxy error page, not just from the server this mirrors.
      return params.flagType === undefined
        ? t('safety:flag.refusal.typeNotDocumentableUnnamed')
        : t('safety:flag.refusal.typeNotDocumentable', {
            type: flagTypeLabel(params.flagType, t),
          })

    case 'FLAG_TYPE_NOT_ALLOWED':
      // A type on a correction. The type is in the record and cannot move, so this is the form and
      // the route disagreeing rather than anything the clinician chose.
      return t('safety:refusal.supersedesWrong')

    case 'FLAG_STATUS_INVALID':
      return t('safety:flag.refusal.statusInvalid')

    case 'FLAG_STATUS_NOT_ALLOWED':
      return t('safety:flag.refusal.statusNotAllowed')

    case 'FLAG_VALUE_MISSING':
      // Branched on the type, because *a weight is missing* and *an isolation type is missing* are
      // different sentences and the clinician is looking at one field, not at a schema.
      switch (params.flagType) {
        case 'weight':
          return t('safety:flag.refusal.weightMissing')
        case 'isolation':
          return t('safety:flag.refusal.isolationTypeMissing')
        default:
          return t('safety:flag.refusal.valueMissing')
      }

    case 'FLAG_WEIGHT_COMMA':
      // The message says which key to press. A comma as a decimal separator in a clinical value is a
      // factor-of-ten risk (AGENTS.md §5), so it is refused rather than repaired — a client that
      // rewrote it would be deciding what the clinician meant.
      return t('safety:flag.refusal.weightComma')

    case 'FLAG_WEIGHT_MALFORMED':
      return t('safety:flag.refusal.weightMalformed')

    case 'FLAG_WEIGHT_NOT_POSITIVE':
      return t('safety:flag.refusal.weightNotPositive')

    case 'FLAG_ALREADY_DOCUMENTED':
      // #46 point 10. Not a dead end: the patient has one of these already, and wanting to document
      // a second is wanting to change the first. The screen turns this into an offer to correct it,
      // using `params.flagId`; this sentence is what it says while making the offer.
      // Typeless wording for the same reason as above, and it matters more here: this sentence is
      // rendered **next to a button** that opens the flag it is talking about, and a gap in it where
      // the type should be reads as a screen that has lost track of what it is offering.
      return params.flagType === undefined
        ? t('safety:flag.refusal.alreadyDocumentedUnnamed')
        : t('safety:flag.refusal.alreadyDocumented', {
            type: flagTypeLabel(params.flagType, t),
          })

    case 'FLAG_FIELD_TOO_LONG':
      return t('safety:refusal.fieldTooLong', {
        field: flagFieldLabel(params.field ?? '', t),
        maximum: params.max ?? 0,
      })

    case 'FLAG_NOT_FOUND':
      return t('safety:flag.refusal.notFound')

    case 'FLAG_RETRACTED':
      return t('safety:flag.refusal.retracted')

    case 'FLAG_VERSION_STALE':
      return t('safety:refusal.versionStale')

    case 'FLAG_NO_CHANGE':
      return t('safety:refusal.noChange')

    case 'FLAG_SUPERSEDES_MISSING':
    case 'FLAG_SUPERSEDES_NOT_ALLOWED':
      return t('safety:refusal.supersedesWrong')

    case 'PATIENT_NOT_FOUND':
      return t('safety:refusal.patientNotFound')

    case 'PATIENT_RETRACTED':
      return t('safety:refusal.patientRetracted')

    case 'ROLE_MAY_NOT_DOCUMENT':
      return t('safety:refusal.roleMayNotDocument')

    case 'FLAG_BODY_MISSING':
    case 'UNKNOWN':
      return t('safety:flag.refusal.unknown')
  }
}

/**
 * What to tell the clinician when the write could not be attempted, or answered something unusable.
 *
 * Separate wording from every refusal, because the action is opposite: a refusal is something to fix
 * in the form, and this is something to wait out — so the message says to leave the form open. What
 * was typed is nowhere else.
 */
export function safetyUnavailableMessage(t: SafetyTranslate): string {
  return t('safety:refusal.unavailable')
}

/** The field names a "too long" refusal can arrive with, for the tests that assert each is worded. */
export const SAFETY_FIELD_NAMES = {
  allergy: ALLERGY_FIELDS,
  flag: FLAG_FIELDS,
} as const
